/**
 * OrdenDetailSheet - Vista detallada de una orden
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TestTubes, FileText, CreditCard, Clock, Trash2, Plus, Pencil } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";

import {
  useOrden,
  useGenerarReciboPdf,
  usePagosOrden,
  useAnularPagoEspecifico,
} from "@/hooks/use-ordenes";
import { useApiError } from "@/hooks/use-api-error";
import { useAuth } from "@/contexts/auth-context";
import { queryKeys } from "@/lib/query-client";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { OrdenStatusFlow } from "./orden-status-flow";
import { OrdenStateActions } from "./orden-state-actions";
import { EstadoOrdenBadge } from "./estado-orden-badge";
import { TipoCobroBadge } from "./tipo-cobro-badge";
import { TipoServicioBadge } from "@/components/tipo-servicio-badge";
import {
  canPagarOrden,
  canAnularPagoOrden,
  debeMostrarResumenPagos,
} from "../utils/pago-orden-logic";
import { PagoOrdenFormDialog } from "./pago-orden-form-dialog";
import { OrdenEditFormDialog } from "./orden-edit-form-dialog";
import { shouldShowEditButton, getMotivoNoEditable } from "../utils/orden-edit-utils";
import type { PagoOrden, PagoOrdenResult, TipoServicio } from "@/hooks/use-ordenes";

interface OrdenDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ordenId: number;
}

function getExamenEstadoBadgeVariant(
  estado: string
): "default" | "secondary" | "destructive" | "outline" {
  switch (estado) {
    case "VALIDADO":
      return "default";
    case "CANCELADO":
      return "destructive";
    case "CON_RESULTADO":
      return "secondary";
    default:
      return "outline";
  }
}

export function OrdenDetailSheet({ open, onOpenChange, ordenId }: OrdenDetailSheetProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { getErrorMessage } = useApiError();
  const { hasPermission } = useAuth();
  const { data: orden, isLoading } = useOrden(ordenId, { enabled: open });
  const { data: pagos = [], isLoading: loadingPagos } = usePagosOrden(ordenId, { enabled: open });
  const descargarRecibo = useGenerarReciboPdf();

  const [isPagoFormOpen, setIsPagoFormOpen] = useState(false);
  const [pagoToAnular, setPagoToAnular] = useState<PagoOrden | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);

  const anularPagoMutation = useAnularPagoEspecifico({
    onSuccess: () => {
      toast.success(t("ordenes.pago.anularPagoExito"));
      setPagoToAnular(null);
      queryClient.invalidateQueries({ queryKey: queryKeys.ordenes.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.ordenes.pagos(ordenId) });
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const handleTransitionSuccess = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.ordenes.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.ordenes.detail(ordenId) });
  };

  const handlePagoSuccess = (_result: PagoOrdenResult) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.ordenes.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.ordenes.detail(ordenId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.ordenes.pagos(ordenId) });
  };

  // Determinar si se puede registrar un nuevo pago
  const ordenParaPago = orden
    ? {
        estado: orden.estado,
        tipo_cobro: orden.tipo_cobro,
        saldo_pendiente: orden.saldo_pendiente,
        total: String(orden.examenes.reduce((s, e) => s + Number(e.precio), 0)),
      }
    : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[540px] overflow-y-auto p-0">
        <SheetHeader className="px-4 sm:px-6 pt-6 pb-0">
          <SheetTitle>{t("ordenes.orderDetail")}</SheetTitle>
          <SheetDescription>
            {isLoading ? (
              <Skeleton className="h-4 w-48" />
            ) : (
              `${t("ordenes.orderNumber")}: ${orden?.numero}`
            )}
          </SheetDescription>
          {/* Edit button / not-editable message */}
          {!isLoading && orden && (
            <div className="pt-2">
              {shouldShowEditButton(!!orden.es_editable, hasPermission("ordenes", "update")) ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 text-xs"
                  onClick={() => setIsEditOpen(true)}
                >
                  <Pencil className="h-3.5 w-3.5 mr-1.5" />
                  {t("ordenes.edit.button")}
                </Button>
              ) : (
                (() => {
                  const motivoKey = getMotivoNoEditable(orden.tipo_cobro, orden.estado);
                  return motivoKey ? (
                    <Badge variant="secondary" className="text-xs font-normal">
                      {t(motivoKey)}
                    </Badge>
                  ) : null;
                })()
              )}
            </div>
          )}
        </SheetHeader>

        {isLoading ? (
          <div className="px-4 sm:px-6 py-4 space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        ) : orden ? (
          <div className="px-4 sm:px-6 pb-6 space-y-5">
            {/* Info general - 1 col en móvil, 2 en desktop */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <div>
                <p className="text-muted-foreground text-xs">{t("ordenes.columns.patient")}</p>
                <p className="font-medium mt-0.5">{orden.paciente_nombre}</p>
                {(orden.paciente_edad_texto || orden.paciente_sexo_display) && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {[orden.paciente_edad_texto, orden.paciente_sexo_display]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                )}
              </div>
              <div>
                <p className="text-muted-foreground text-xs">{t("ordenes.columns.date")}</p>
                <p className="font-medium mt-0.5">{new Date(orden.fecha).toLocaleDateString()}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">{t("ordenes.columns.status")}</p>
                <EstadoOrdenBadge
                  estado={orden.estado}
                  label={orden.estado_display}
                  className="mt-1"
                />
              </div>
              <div>
                <p className="text-muted-foreground text-xs">{t("ordenes.columns.priority")}</p>
                <p className="font-medium mt-0.5">{orden.prioridad_display}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">{t("ordenes.fields.serviceType")}</p>
                <TipoServicioBadge tipo={orden.tipo_servicio as TipoServicio} className="mt-1" />
              </div>
              <div className="sm:col-span-2">
                <p className="text-muted-foreground text-xs">{t("ordenes.fields.doctor")}</p>
                <p className="font-medium mt-0.5">
                  {orden.medico_remitente_nombre ?? t("ordenes.noDoctor", "No especificado")}
                </p>
              </div>
              {orden.cliente_nombre && (
                <div className="sm:col-span-2">
                  <p className="text-muted-foreground text-xs">{t("ordenes.fields.client")}</p>
                  <p className="font-medium mt-0.5">{orden.cliente_nombre}</p>
                </div>
              )}
              <div className="sm:col-span-2">
                <p className="text-muted-foreground text-xs">{t("ordenes.listaPrecio.label")}</p>
                {orden.lista_precio_nombre ? (
                  <div className="flex items-center gap-2 mt-0.5">
                    <p className="font-medium">{orden.lista_precio_nombre}</p>
                    {orden.lista_precio_porcentaje !== null && (
                      <Badge variant="secondary" className="text-xs">
                        {orden.lista_precio_porcentaje >= 0 ? "+" : ""}
                        {orden.lista_precio_porcentaje}%
                      </Badge>
                    )}
                  </div>
                ) : (
                  <p className="font-medium mt-0.5 text-muted-foreground">
                    {t("ordenes.listaPrecio.standard")}
                  </p>
                )}
              </div>
              <div>
                <p className="text-muted-foreground text-xs">{t("ordenes.fields.deliveryType")}</p>
                <p className="font-medium mt-0.5">{orden.tipo_entrega_display}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">{t("ordenes.columns.branch")}</p>
                <p className="font-medium mt-0.5">{orden.sucursal_nombre}</p>
              </div>
            </div>

            {/* Status flow indicator */}
            <OrdenStatusFlow estado={orden.estado} />

            {/* State transition actions */}
            <OrdenStateActions orden={orden} onTransitionSuccess={handleTransitionSuccess} />

            {/* Recibo PDF */}
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => descargarRecibo.mutate(orden.id)}
              disabled={descargarRecibo.isPending}
            >
              <FileText className="h-4 w-4 mr-2" />
              {descargarRecibo.isPending
                ? t("ordenes.recibos.generating")
                : t("ordenes.recibos.download")}
            </Button>

            {/* Sección de pago */}
            <div className="rounded-lg border p-3 text-sm space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 font-medium">
                  <CreditCard className="h-4 w-4 text-muted-foreground" />
                  {t("ordenes.pago.historialPagos")}
                </div>
                {ordenParaPago && canPagarOrden(ordenParaPago) && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    onClick={() => setIsPagoFormOpen(true)}
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    {t("ordenes.pago.registrarPago")}
                  </Button>
                )}
              </div>

              {/* Badge tipo cobro — esperar a que pagos cargue para mostrar estado correcto */}
              <div>
                {loadingPagos ? (
                  <Skeleton className="h-5 w-24 rounded-full" />
                ) : (
                  <TipoCobroBadge
                    tipoCobro={orden.tipo_cobro}
                    pagos={pagos}
                    esCortesia={orden.es_cortesia}
                    context="detail-sheet"
                  />
                )}
              </div>

              {/* Resumen total / pagado / saldo — visible con pago directo completo O con pagos parciales */}
              {!loadingPagos && debeMostrarResumenPagos(orden, pagos) && (
                <div className="space-y-1 text-xs border-t pt-2">
                  <div className="flex justify-between text-muted-foreground">
                    <span>{t("ordenes.pago.totalOrden")}</span>
                    <span className="font-mono">
                      L.
                      {parseFloat(orden.total_pagado || "0") > 0
                        ? (
                            parseFloat(orden.total_pagado) + parseFloat(orden.saldo_pendiente)
                          ).toFixed(2)
                        : "—"}
                    </span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>{t("ordenes.pago.totalPagado")}</span>
                    <span className="font-mono text-green-600">
                      L.{parseFloat(orden.total_pagado || "0").toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between font-medium">
                    <span>{t("ordenes.pago.saldoPendiente")}</span>
                    <span
                      className={`font-mono ${parseFloat(orden.saldo_pendiente || "0") > 0 ? "text-orange-600" : "text-green-600"}`}
                    >
                      L.{parseFloat(orden.saldo_pendiente || "0").toFixed(2)}
                    </span>
                  </div>
                </div>
              )}

              {/* Lista de pagos */}
              {loadingPagos ? (
                <div className="space-y-2">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : pagos.length > 0 ? (
                <div className="space-y-2">
                  {pagos.map((pago) => (
                    <div
                      key={pago.id}
                      className={`flex items-start justify-between gap-2 rounded-md border p-2 text-xs ${!pago.is_active ? "opacity-50" : ""}`}
                    >
                      <div className="space-y-0.5 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                            {pago.tipo_pago_display}
                          </Badge>
                          <span className="font-medium">{pago.metodo_nombre}</span>
                          {!pago.is_active && (
                            <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                              {t("common.inactive")}
                            </Badge>
                          )}
                        </div>
                        <div className="text-muted-foreground">
                          {new Date(pago.created_at).toLocaleDateString()} —{" "}
                          <span className="font-mono font-medium text-foreground">
                            L.{parseFloat(pago.monto).toFixed(2)}
                          </span>
                          {parseFloat(pago.vuelto) > 0 && (
                            <span className="ml-1 text-green-600">
                              ({t("ordenes.pago.vuelto")}: L.{parseFloat(pago.vuelto).toFixed(2)})
                            </span>
                          )}
                        </div>
                        {pago.referencia && (
                          <p className="text-muted-foreground truncate">
                            {t("ordenes.pago.reference")}: {pago.referencia}
                          </p>
                        )}
                      </div>
                      {pago.is_active && orden && canAnularPagoOrden(pago, orden) && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-6 w-6 shrink-0 text-destructive hover:text-destructive"
                          onClick={() => setPagoToAnular(pago)}
                          data-testid={`anular-pago-btn-${pago.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-xs">
                  {orden.tipo_cobro === "FACTURA"
                    ? t("ordenes.pago.managedByInvoice")
                    : t("ordenes.pago.noPagos")}
                </p>
              )}

              {/* Pendiente de pago */}
              {orden.tipo_cobro === null && pagos.length === 0 && (
                <div className="flex items-center gap-1.5 text-muted-foreground text-xs">
                  <Clock className="h-3.5 w-3.5" />
                  <span>{t("ordenes.pago.pendingPayment")}</span>
                </div>
              )}
            </div>

            {orden.observaciones && (
              <>
                <Separator />
                <div className="text-sm">
                  <p className="text-muted-foreground text-xs">
                    {t("ordenes.fields.observations")}
                  </p>
                  <p className="mt-1 text-muted-foreground/80 leading-relaxed">
                    {orden.observaciones}
                  </p>
                </div>
              </>
            )}

            <Separator />

            {/* Exámenes */}
            {(() => {
              const porcentaje = Number(orden.lista_precio_porcentaje ?? 0);
              const tieneAjuste = porcentaje !== 0;
              // e.precio ya viene ajustado por la lista de precios desde el backend
              const subtotalAjustado = orden.examenes.reduce((s, e) => s + Number(e.precio), 0);
              // Precio base = precio ajustado / (1 + pct/100)
              const subtotalBase = tieneAjuste
                ? Math.round((subtotalAjustado / (1 + porcentaje / 100)) * 100) / 100
                : subtotalAjustado;
              const ajuste = subtotalAjustado - subtotalBase;
              const descPct = Number(orden.descuento_porcentaje ?? 0);
              const descLps = Number(orden.descuento_lempiras ?? 0);
              const afterDescPct = subtotalAjustado * (1 - descPct / 100);
              const total = Math.max(afterDescPct - descLps, 0);

              return (
                <div>
                  <h3 className="text-sm font-medium mb-3">
                    {t("ordenes.examList")} ({orden.total_examenes})
                  </h3>
                  <div className="space-y-1.5">
                    {orden.examenes.map((ex) => {
                      const precioAjustado = Number(ex.precio);
                      // precio base = precio ajustado / (1 + pct/100)
                      const precioBase = tieneAjuste
                        ? Math.round((precioAjustado / (1 + porcentaje / 100)) * 100) / 100
                        : precioAjustado;
                      return (
                        <div
                          key={ex.id}
                          className="p-2.5 border rounded-lg text-sm bg-muted/30 space-y-1"
                        >
                          {/* Fila 1: código + nombre */}
                          <div className="flex items-center gap-2 min-w-0">
                            <TestTubes className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                            <span className="font-mono text-[11px] text-muted-foreground shrink-0">
                              {ex.examen_tipo_codigo}
                            </span>
                            <span className="truncate">{ex.examen_tipo_nombre}</span>
                          </div>
                          {/* Fila 2: perfil + precio + estado */}
                          <div className="flex items-center justify-between gap-2 pl-5">
                            <div>
                              {ex.perfil_origen_nombre && (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                  {ex.perfil_origen_nombre}
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              {tieneAjuste ? (
                                <span className="flex items-center gap-1">
                                  <span className="text-xs text-muted-foreground line-through tabular-nums">
                                    L {precioBase.toFixed(2)}
                                  </span>
                                  <span
                                    className={`text-xs font-medium tabular-nums ${porcentaje > 0 ? "text-orange-600" : "text-green-600"}`}
                                  >
                                    L {precioAjustado.toFixed(2)}
                                  </span>
                                </span>
                              ) : (
                                <span className="text-xs text-muted-foreground tabular-nums">
                                  L {precioBase.toFixed(2)}
                                </span>
                              )}
                              <Badge
                                variant={getExamenEstadoBadgeVariant(ex.estado)}
                                className="text-[11px]"
                              >
                                {ex.estado_display}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Resumen de totales */}
                  <div className="mt-3 pt-3 border-t space-y-1 text-sm">
                    {/* Subtotal base — siempre visible */}
                    <div className="flex justify-between text-muted-foreground">
                      <span>{t("ordenes.summary.subtotal")}</span>
                      <span className="tabular-nums">L {subtotalBase.toFixed(2)}</span>
                    </div>

                    {/* Ajuste lista de precios */}
                    {tieneAjuste && (
                      <>
                        <div
                          className={`flex justify-between text-xs ${ajuste > 0 ? "text-orange-600" : "text-green-600"}`}
                        >
                          <span>
                            {orden.lista_precio_nombre} ({porcentaje >= 0 ? "+" : ""}
                            {porcentaje}%)
                          </span>
                          <span className="tabular-nums">
                            {ajuste > 0 ? "+" : "- "}L {Math.abs(ajuste).toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between font-medium">
                          <span>{t("ordenes.summary.subtotalAdjusted")}</span>
                          <span className="tabular-nums">L {subtotalAjustado.toFixed(2)}</span>
                        </div>
                      </>
                    )}

                    {/* Descuentos */}
                    {descPct > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>
                          {t("ordenes.discount.percentage")} ({descPct}%)
                        </span>
                        <span className="tabular-nums">
                          - L {(subtotalAjustado * (descPct / 100)).toFixed(2)}
                        </span>
                      </div>
                    )}
                    {descLps > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>{t("ordenes.discount.lempiras")}</span>
                        <span className="tabular-nums">- L {descLps.toFixed(2)}</span>
                      </div>
                    )}

                    {/* Total */}
                    <div className="flex justify-between pt-1 border-t font-semibold">
                      <span>Total</span>
                      <span className="tabular-nums">L {total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        ) : null}
      </SheetContent>

      {/* Dialog registrar pago */}
      {orden && (
        <PagoOrdenFormDialog
          open={isPagoFormOpen}
          onOpenChange={setIsPagoFormOpen}
          orden={
            {
              ...orden,
              total: String(orden.examenes.reduce((s, e) => s + Number(e.precio), 0)),
            } as unknown as import("@/hooks/use-ordenes").OrdenListItem
          }
          onPagoSuccess={handlePagoSuccess}
        />
      )}

      {/* Dialog editar orden */}
      <OrdenEditFormDialog open={isEditOpen} onOpenChange={setIsEditOpen} ordenId={ordenId} />

      {/* Confirm anular pago específico */}
      <ConfirmDialog
        open={!!pagoToAnular}
        onOpenChange={(open) => !open && setPagoToAnular(null)}
        title={t("ordenes.pago.anularPago")}
        description={
          pagoToAnular
            ? t("ordenes.pago.anularPagoConfirmDetalle", {
                tipoPago: pagoToAnular.tipo_pago_display,
                monto: parseFloat(pagoToAnular.monto).toFixed(2),
                metodo: pagoToAnular.metodo_nombre,
                fecha: new Date(pagoToAnular.created_at).toLocaleDateString(),
              })
            : ""
        }
        onConfirm={() =>
          pagoToAnular && anularPagoMutation.mutate({ ordenId, pagoId: pagoToAnular.id })
        }
        variant="destructive"
        isLoading={anularPagoMutation.isPending}
      />
    </Sheet>
  );
}

// NOTE: dialogs appended below the Sheet closing tag intentionally — they render in portal
